import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Grid } from '@mui/material'

import {
  formatTravelMinutes,
  getTravelMinutes,
  getTravelPay,
} from '../../helpers/shiftTravel'

// Paid travel time either side of the shift at its own hourly rate
// (e.g. 90 minutes each way at £12.50 around a clinical shift at the role rate).
//
// Controlled editor:
// `travel` is { beforeMinutes, afterMinutes, rate } or null when the shift
// has no paid travel.
//
// `onChange` receives the replacement value.
//
// The shift's start/end are untouched — travel only adds pay.
const ShiftTravelEditor = ({
  travel,
  onChange,
  disabled = false,
  currency = '£',
  defaultRate,
}) => {
  const active =
    !!travel &&
    (travel.beforeMinutes !== undefined ||
      travel.afterMinutes !== undefined ||
      travel.rate !== undefined)

  const update = (patch) => {
    onChange({
      ...(travel ?? {}),
      ...patch,
    })
  }

  /**
   * Keeps the field value as a string while the user is typing.
   *
   * This is important for decimal values. Converting with parseFloat()
   * on every keystroke causes values such as "2." to immediately become
   * "2", preventing the user from naturally typing "2.56".
   */
  const numberField = (onValueChange) => (e) => {
    const next = e.target.value

    if (next === '') {
      onValueChange('')
      return
    }

    const numericValue = Number(next)

    if (!Number.isNaN(numericValue) && numericValue >= 0) {
      onValueChange(next)
    }
  }

  if (!active) {
    return (
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            onChange({
              beforeMinutes: '',
              afterMinutes: '',
              rate: defaultRate ?? '',
            })
          }
          disabled={disabled}
          size="small"
        >
          Add paid travel time
        </Button>
      </Box>
    )
  }

  const totalMinutes = getTravelMinutes(travel)
  const travelPay = getTravelPay(travel)

  return (
    <Stack spacing={0.5}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5.5}>
            <TextField
              label="Travel before"
              type="number"
              value={travel.beforeMinutes ?? ''}
              onChange={numberField((beforeMinutes) =>
                update({ beforeMinutes }),
              )}
              onWheel={(e) => e.target.blur()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mins</InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 5,
              }}
              disabled={disabled}
              size="small"
              sx={{
                flex: '1 1 130px',
                minWidth: 130,
              }}
            />
          </Grid>

          <Grid item xs={12} lg={5.5}>
            <TextField
              label="Travel after"
              type="number"
              value={travel.afterMinutes ?? ''}
              onChange={numberField((afterMinutes) =>
                update({ afterMinutes }),
              )}
              onWheel={(e) => e.target.blur()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mins</InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 5,
              }}
              disabled={disabled}
              size="small"
              sx={{
                flex: '1 1 130px',
                minWidth: 130,
              }}
            />
          </Grid>

          <Grid item xs={12} lg={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <IconButton
                onClick={() => onChange(null)}
                disabled={disabled}
                size="small"
                aria-label="Remove paid travel time"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Travel rate"
              type="number"
              value={travel.rate ?? ''}
              onChange={numberField((rate) => update({ rate }))}
              onWheel={(e) => e.target.blur()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {currency}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">/hr</InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              disabled={disabled}
              size="small"
              fullWidth
              helperText={
                totalMinutes
                  ? `${formatTravelMinutes(
                      totalMinutes,
                    )} paid travel = ${currency}${travelPay.toFixed(
                      2,
                    )}. Shift times stay as the on-site window; travel is paid on top.`
                  : 'Paid on top of the shift rate; the shift times stay as the on-site window.'
              }
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export default ShiftTravelEditor